import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Modal, notification, Row, Typography } from "antd";
import './NmiComponent.scss';

declare global {
  interface Window {
    CollectJS: any;
  }
}

interface NMIProps {
  tokenizationKey: string;
  hotelName: string;
  amount: number;
  currency: string;
  open: boolean;
  onSuccess: (paymentToken: string) => Promise<void>;
  onCancel: () => void;
}

const NMIComponent = (props: NMIProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const configuredRef = useRef(false);
  const [fieldsValid, setFieldsValid] = useState<Record<string, boolean>>({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });

  const [showFieldsError, setShowFieldsError] = useState(false);

  const allFieldsValid = Object.values(fieldsValid).every(Boolean);

  useEffect(() => {
    if (allFieldsValid) {
      setShowFieldsError(false);
    }
  }, [allFieldsValid]);

  useEffect(() => {
    if (!props.open) {
      configuredRef.current = false;
      setLoading(false);
      setFieldsValid({ ccnumber: false, ccexp: false, cvv: false });
      return;
    }

    configuredRef.current = false;

    const configureCollectJS = () => {
      const interval = setInterval(() => {
        if (window.CollectJS && !configuredRef.current) {
          configuredRef.current = true;
          clearInterval(interval);

          window.CollectJS.configure({
            paymentSelector: "#nmiPayButton",
            variant: "inline",
            paymentType: "cc",
            customCss: {
              "border": "1px solid #d9d9d9",
              "border-radius": "8px",
              "padding": "10px 12px",
              "height": "42px",
              "box-sizing": "border-box",
              "font-size": "14px",
              "font-family": "Montserrat, sans-serif",
              "background-color": "#fff",
              "outline": "none",
              "transition": "border-color 0.3s",
            },
            focusCss: {
              "border-color": "#40a9ff",
              "outline": "none",
            },
            invalidCss: {
              "border-color": "#ff4d4f",
            },
            fields: {
              ccnumber: {
                selector: "#ccnumber",
                placeholder: "0000 0000 0000 0000",
                title: "Card Number"
               
              },
              ccexp: {
                selector: "#ccexp",
                placeholder: "MM / YY",
                title: "Expiry Date",
              },
              cvv: {
                selector: "#cvv",
                placeholder: "CVV",
                title: "CVV",
              },
            },

            validationCallback: (field: string, status: boolean) => {
              setFieldsValid((prev) => ({ ...prev, [field]: status }));
            },
            callback: async (response: any) => {
              if (response.token) {
                try {
                  await props.onSuccess(response.token);
                  setLoading(false);
                } catch (err) {
                  setLoading(false);
                  notification.error({
                    message:
                      t("components.nmi.preauth-error") ||
                      "Error al procesar el pago con el token",
                  });
                }
              } else {
                setLoading(false);
                notification.error({
                  message:
                    response.error ||
                    t("components.nmi.tokenization-error") ||
                    "Error al procesar el pago",
                });
              }
            },
          });
        }
      }, 100);
    };

    // Si el script ya existe, solo reconfiguramos
    const existingScript = document.getElementById("collectjs-script");
    if (existingScript && window.CollectJS) {
      configureCollectJS();
    } else {
      // Eliminar script previo si existe pero CollectJS no está disponible
      if (existingScript) existingScript.remove();
      delete window.CollectJS;

      const script = document.createElement("script");
      script.id = "collectjs-script";
      script.src = "https://secure.nmi.com/token/Collect.js";
      script.setAttribute("data-tokenization-key", props.tokenizationKey);
      script.setAttribute("data-payment-type", "cc");
      script.async = true;

      script.onload = () => {
        configureCollectJS();
      };

      document.head.appendChild(script);
    }

    return () => {
      configuredRef.current = false;
    };
  }, [props.open, props.tokenizationKey]);

  return (
    <Modal
      className="app-modal"
      title={t("components.nmi.payment-title") || "Pago con tarjeta"}
      open={props.open}
      onCancel={props.onCancel}
      footer={null}
      destroyOnClose
    >
      <div className="nmi-component">
        <div className="nmi-payment-info">
          <Typography.Text strong>{props.hotelName}</Typography.Text>
          <Typography.Title level={3} style={{ margin: "8px 0 24px" }}>
            {props.amount.toFixed(2)} {props.currency}
          </Typography.Title>
        </div>

        <Row gutter={[0, 16]}>
          <Col xs={24}>
            <label className="nmi-label">{t("components.nmi.card-number") || "N\u00famero de tarjeta"}</label>
            <div id="ccnumber" className="nmi-field" />
          </Col>
          <Col xs={12} style={{ paddingRight: 8 }}>
            <label className="nmi-label">{t("components.nmi.expiry-date") || "Caducidad"}</label>
            <div id="ccexp" className="nmi-field" />
          </Col>
          <Col xs={12} style={{ paddingLeft: 8 }}>
            <label className="nmi-label">CVV</label>
            <div id="cvv" className="nmi-field" />
          </Col>
          <Col xs={24} style={{ marginTop: 8 }}>
            {showFieldsError && (
              <div style={{ color: '#ff4d4f', marginBottom: 8, fontSize: 14 }}>
                {t("components.nmi.invalid-fields") || "Los datos de la tarjeta no son correctos. Revísalos e inténtalo de nuevo."}
              </div>
            )}
            <Button
              id="nmiPayButton"
              type="primary"
              className="nmi-pay-button"
              block
              size="large"
              loading={loading}
              disabled={loading || !allFieldsValid}
              onClick={() => {
                if (!allFieldsValid) {
                  setShowFieldsError(true);
                  return;
                }
                setLoading(true);
              }}
            >
              {t("components.nmi.pay-button") || "Pagar"} {props.amount.toFixed(2)} {props.currency}
            </Button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default NMIComponent;